import type { ConsoleEntry } from "../types";

const NIVEIS = ["log", "info", "warn", "error", "debug"] as const;

// Redação best-effort de segredos comuns antes de guardar/enviar. Não é
// à prova de tudo — reduz o risco de vazar credenciais no log.
const SEGREDOS = [
  /bearer\s+[\w.-]+/gi,
  /eyJ[\w-]+\.[\w-]+\.[\w-]+/g, // JWT
  /\b(sk|pk|altw|altk)_[A-Za-z0-9_]{6,}\b/g,
  /\b(password|senha|token|secret)\b\s*[:=]\s*\S+/gi,
];

export function redigir(s: string): string {
  return SEGREDOS.reduce((acc, re) => acc.replace(re, "«redigido»"), s);
}

/** Serializa um argumento de console para uma string segura (redigida, sem ciclos). */
export function serializarArg(a: unknown): string {
  if (typeof a === "string") return redigir(a);
  if (a instanceof Error) return redigir(a.stack || a.message || String(a));
  try {
    const vistos = new WeakSet<object>();
    return redigir(
      JSON.stringify(a, (_k, v) => {
        if (typeof v === "object" && v !== null) {
          if (vistos.has(v)) return "[Circular]";
          vistos.add(v);
        }
        return v as unknown;
      }) ?? String(a),
    );
  } catch {
    return String(a);
  }
}

type FnConsole = (...args: unknown[]) => void;

/** Buffer circular do console + erros globais, ativo desde o load. O(1) por push. */
export class ConsoleBuffer {
  private buf: ConsoleEntry[] = [];
  private restauradores: Array<() => void> = [];

  constructor(private readonly max: number) {}

  private push(level: string, args: unknown[]): void {
    this.buf.push({ level, args: args.map(serializarArg), ts: Date.now() });
    if (this.buf.length > this.max) this.buf.splice(0, this.buf.length - this.max);
  }

  iniciar(): void {
    const c = console as unknown as Record<string, FnConsole | undefined>;
    for (const n of NIVEIS) {
      const orig = c[n];
      if (typeof orig !== "function") continue;
      const ligado = orig.bind(console);
      c[n] = (...args: unknown[]) => {
        this.push(n, args);
        ligado(...args);
      };
      this.restauradores.push(() => {
        c[n] = orig; // restaura a REFERÊNCIA original (não a cópia bound)
      });
    }

    const onErro = (e: ErrorEvent) => this.push("error", [e.message, `${e.filename}:${e.lineno}`]);
    const onRej = (e: PromiseRejectionEvent) => this.push("error", ["unhandledrejection", e.reason]);
    window.addEventListener("error", onErro);
    window.addEventListener("unhandledrejection", onRej);
    this.restauradores.push(() => {
      window.removeEventListener("error", onErro);
      window.removeEventListener("unhandledrejection", onRej);
    });
  }

  /** Cópia das linhas capturadas (mais antigas → mais recentes). */
  snapshot(): ConsoleEntry[] {
    return this.buf.slice();
  }

  destruir(): void {
    for (const r of this.restauradores) r();
    this.restauradores = [];
  }
}
